import { useCallback, useEffect } from "react"
import { batch, useSignal } from "@preact/signals-react"
import { getCurrentDate, getNextDate, getSubmit, isEmpty, postSubmit } from "@ui-seeker/utils"

import { BACKEND_APIS } from "@constants"
import { nextCheck, userData } from "@signals"
import { useSearchParams, useNavigate } from "react-router-dom"
import { useLocalStorage } from "./index"


export default function useUserApp(needCheck = false) {
	const [searchParams] = useSearchParams()
	const navigate = useNavigate()
	const checkingLogging = useSignal(false)
	const isLogged = useSignal(nextCheck.value && nextCheck.value >= getCurrentDate().getTime())
	const isLoadingLogin = useSignal(false)
	const error = useSignal(null)

	const { clearUserData } = useLocalStorage()

	const setUserData = newData => batch(() => {
		userData.value = newData
		nextCheck.value = getNextDate({ hours: 1 }).getTime()
		isLogged.value = true
	})

	useEffect(() => {
		if (searchParams.get("crud")) handleLogin(null, null, searchParams.get("crud"))
	}, [searchParams.get("crud")])

	useEffect(() => batch(() => {
		if(needCheck) {
			checkingLogging.value = true
			getSubmit(`${BACKEND_APIS.SESSION}/check-login`)
				.then(res => setUserData(res))
				.catch((err) => batch(() => {
					if (err && err.status === 401) {
						if (!isEmpty(userData.value)) userData.value = {}
						if (nextCheck.value) nextCheck.value = null
						isLogged.value = false
						clearUserData()
						const existingSearch = searchParams.toString() ? `?${searchParams.toString()}` : ''
						navigate(`/login${existingSearch}`, { replace: true })
					} else {
						if (!isEmpty(userData.value)) userData.value = {}
					}
				}))
				.finally(() => checkingLogging.value = false)
		}
	}), [])

	const handleLogin = useCallback((username = null, password = null, crud = "") => {
		isLoadingLogin.value = true
		postSubmit('/api/login', {
			username, password, crud
		}).then(res => batch(() => {
			if (res.error)
				error.value = "Invalid username or password entered."
			else {
				if (error.value)
					error.value = null
				setUserData(res)
			}
		})).catch(() => {
			if (nextCheck.value)
				nextCheck.value = null
			error.value = 'Server call failed. Please try again.'
		}).finally(() => isLoadingLogin.value = false)
	}, [])

	const handleLogout = useCallback(() => {
		getSubmit(`${BACKEND_APIS.SESSION}/logout`)
			.finally(() => {
				batch(() => {
					if (!isEmpty(userData.value)) userData.value = {}
					if (nextCheck.value) nextCheck.value = null
				})
				clearUserData()
				navigate('/login', { replace: true })
			})
	}, [])

	return {
		checkingLogging: checkingLogging.value,
		isLogged: isLogged.value,
		isLoadingLogin: isLoadingLogin.value,
		error: error.value,

		handleLogin, handleLogout
	}
}